import { useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import {
  TRX_STATUS_ICON_MAP,
  TRX_STATUS_LABEL_MAP,
} from '@root/entities/transaction/ui/constants'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { useTranslation } from '@root/shared/localization'

import type { WalletPositionModel } from '@root/entities/position'
import type { Transaction } from '@root/entities/transaction'
import type { MultipleSwapTransaction } from '@root/features/trade/swap'

type Props = {
  tx: MultipleSwapTransaction | undefined
  errorMessage?: string
  position: WalletPositionModel
} & React.PropsWithChildren

export const SellPositionSnackbarItem = ({
  tx,
  position,
  errorMessage,
}: Props) => {
  const { t } = useTranslation()
  const [wallet] = useAtom(position.walletAtom)
  const status_: MultipleSwapTransaction['status'] = tx?.status ?? 'active'
  const txStatus = useMemo(() => {
    return match(status_)
      .returnType<Transaction.Status>()
      .with('finished', () => 'success')
      .with('active', () => 'pending')
      .with('failed', () => 'failed')
      .exhaustive()
  }, [status_])

  const StatusIcon = TRX_STATUS_ICON_MAP[txStatus]

  if (!wallet) {
    return undefined
  }

  return (
    <Root data-status={status_}>
      <Body>
        <WalletName wallet={wallet} size="xs" />
        <Address walletAddress={wallet.address} />
        <Status>
          <StatusIcon size={10} />
          {t(TRX_STATUS_LABEL_MAP[txStatus])}
        </Status>
      </Body>
      {errorMessage ? <Error>{errorMessage}</Error> : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 7px;
  --wallet-short-indicator-size: 3px;

  &[data-status='finished'] {
    --status-color: var(--success-9);
  }
  &[data-status='active'] {
    --status-color: var(--textA-7);
  }
  &[data-status='failed'] {
    --status-color: var(--error-9);
  }
`

const Body = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr 2fr;
  align-items: center;
  gap: 5px;
`

const Error = styled.div`
  background: var(--backgroundA-2);
  color: var(--error-9);
  height: 23px;
  border-radius: 4px;

  font-size: 10px;
  font-weight: 500;
  line-height: 12px;

  display: grid;
  place-content: center;
`

const Address = styled(WalletAddressTruncated)`
  color: var(--textA-10);

  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  text-align: start;
`

const Status = styled.div`
  color: var(--status-color);

  text-align: right;
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;

  gap: 2px;
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: end;
`

const WalletName = styled(WalletModelShort)`
  font-weight: 500;
`
